import { featureCollection, type LineString } from '@turf/helpers';
import type { MapMouseEvent, MapTouchEvent } from 'mapbox-gl';
import { useContext, useEffect, useMemo } from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { LineProperties } from '@alltrails/types/CustomRoute';
import useMap from '../../../hooks/useMap';
import { useAppDispatch, useAppSelector } from '../../../redux';
import { setActiveSegmentIndex, setHoveredSegmentIndex } from '../../../redux/customRoute';
import { updateCursor } from '../../../redux/reducer';
import { hoveredPolylineId } from '../../../utils/layers';
import { MapLayerInteractionsContext } from '../../MapLayerInteractionsProvider';
import getLineStrings from './getLineStrings';

export const getSourceId = (uniqueId?: string) => (uniqueId ? `polyline-source-${uniqueId}` : 'polyline-source');

/**
 * HoveredPolyline renders a transparent layer over ActivePolyline and InactivePolyline components.
 * By having a single transparent layer handle mouse events, we avoid:
 * 1. Duplicate event handlers on each underlying polyline (ActivePolyline renders 3 PolylineSources which is 6 Layers, and InactivePolyline renders 1 PolylineSource which is 2 Layers)
 * 2. Race conditions between multiple hover states
 * 3. Edge cases when hovering between segments
 * This pattern simplifies event handling by centralizing hover logic in one place.
 */

const HoveredPolyline = () => {
  const map = useMap();
  const dispatch = useAppDispatch();
  const { activeSegmentIndex, segments, isEditing } = useAppSelector(state => ({
    activeSegmentIndex: state.customRoute.activeSegmentIndex,
    segments: state.customRoute.route.segments,
    isEditing: state.customRoute.isEditing
  }));
  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  useEffect(() => {
    const onClick = (event: MapMouseEvent) => {
      if (event.features && event.features.length > 0) {
        event.preventDefault();
        const feature = event.features?.[0];
        const properties = feature?.properties as LineProperties;

        // Extract segment index from the feature ID
        // Feature ID format: "segment-${segmentIndex}-path-${pathIndex}"
        const featureId = properties?.id;
        const segmentIndexMatch = featureId?.match(/segment-(\d+)-path-/);

        if (segmentIndexMatch) {
          const segmentIndex = parseInt(segmentIndexMatch[1], 10);
          if (activeSegmentIndex === segmentIndex) {
            dispatch(setActiveSegmentIndex({ activeSegmentIndex: undefined, canEdit: false }));
          } else {
            dispatch(setActiveSegmentIndex({ activeSegmentIndex: segmentIndex, canEdit: false }));
          }
        }
      }
    };

    const onMouseMove = (event: MapMouseEvent) => {
      if (event.features && event.features.length > 0) {
        const feature = event.features?.[0];
        const properties = feature?.properties as LineProperties;
        const featureId = properties?.id;
        const segmentIndexMatch = featureId?.match(/segment-(\d+)-path-/);
        if (segmentIndexMatch) {
          const segmentIndex = parseInt(segmentIndexMatch[1], 10);
          dispatch(setHoveredSegmentIndex(segmentIndex));
        }
      }
      dispatch(updateCursor('pointer'));
    };

    const onMouseLeave = () => {
      dispatch(setHoveredSegmentIndex(undefined));
      dispatch(updateCursor('unset'));
    };

    registerEventHandler(hoveredPolylineId, 'click', onClick);
    registerEventHandler(hoveredPolylineId, 'mousemove', onMouseMove);
    registerEventHandler(hoveredPolylineId, 'mouseleave', onMouseLeave);

    return () => {
      unregisterEventHandler(hoveredPolylineId, 'click', onClick);
      unregisterEventHandler(hoveredPolylineId, 'mousemove', onMouseMove);
      unregisterEventHandler(hoveredPolylineId, 'mouseleave', onMouseLeave);
    };
  }, [activeSegmentIndex, registerEventHandler, unregisterEventHandler, dispatch]);

  useEffect(() => {
    if (isEditing) {
      return;
    }

    const removeActiveStates = (event: MapMouseEvent | MapTouchEvent) => {
      // We prevent default when a user clicks on this layer
      if (!event.defaultPrevented) {
        dispatch(setActiveSegmentIndex({ activeSegmentIndex: undefined, canEdit: false }));
      }
    };

    map?.on('click', removeActiveStates);
    return () => {
      map?.off('click', removeActiveStates);
    };
  }, [dispatch, map, isEditing]);

  const data = useMemo(
    () => featureCollection<LineString, LineProperties>(segments?.map((segment, index) => getLineStrings(segment, undefined, index)).flat()),
    [segments]
  );
  const sourceId = getSourceId(hoveredPolylineId);

  return (
    <Source id={sourceId} type="geojson" data={data} lineMetrics>
      <Layer
        id={hoveredPolylineId}
        source={sourceId}
        type="line"
        layout={{
          'line-join': 'round',
          'line-cap': 'round',
          visibility: isEditing ? 'none' : 'visible'
        }}
        paint={{
          'line-width': 12, // Wider than default for better hover detection
          'line-opacity': 0, // Completely transparent
          'line-color': 'rgba(0, 0, 0, 0)'
        }}
      />
    </Source>
  );
};

export default HoveredPolyline;
